import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem('cp_myrto_session');
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('cp_myrto_session', sid);
  }
  return sid;
};

export const MyrtoChat = ({ frameMax = 1400, framePad = 48 }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = getSessionId();
  const frameRight = `max(calc((100vw - ${frameMax}px) / 2 + ${framePad}px), 8px)`;

  useEffect(() => {
    if (!open || loaded) return;
    axios.get(`${API}/myrto/history`, { params: { session_id: sessionId }, withCredentials: true })
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/myrto/chat`, { message: text, session_id: sessionId }, { withCredentials: true });
      setMessages((m) => [...m, { role: 'assistant', text: (data.reply || '').replace(/\*\*/g, '') }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message : detail;
      toast.error(msg || 'Η Μυρτώ δεν απάντησε. Δοκιμάστε ξανά.');
      setMessages((m) => m.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="myrto-fab"
        onClick={() => setOpen(!open)}
        title="Μυρτώ — βοηθός προγράμματος"
        style={{ right: frameRight }}
        className="fixed z-[70] bottom-6 max-sm:bottom-36 w-12 h-12 rounded-full bg-purple-900 text-white shadow-xl hover:bg-purple-950 transition-all flex items-center justify-center overflow-hidden ring-2 ring-purple-900"
      >
        {open ? <X className="w-5 h-5" /> : <img src="/myrto.jpg" alt="Μυρτώ" className="w-full h-full object-cover" />}
      </button>

      {open && (
        <div
          data-testid="myrto-panel"
          style={{ right: frameRight }}
          className="fixed z-[70] bottom-20 max-sm:bottom-52 max-sm:left-3 w-[380px] max-sm:w-auto max-h-[65vh] bg-white border border-stone-300 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-purple-900 text-white px-4 py-3 flex items-center gap-3">
            <img src="/myrto.jpg" alt="Μυρτώ" className="w-9 h-9 rounded-full object-cover ring-2 ring-purple-300" data-testid="myrto-avatar" />
            <div>
              <p className="text-sm font-semibold leading-tight">Μυρτώ</p>
              <p className="text-[11px] text-purple-200 leading-tight">Βοηθός προγράμματος · 1 μονάδα ανά ερώτηση</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#faf6ef]" style={{ minHeight: 200 }}>
            {messages.length === 0 && !sending && (
              <div className="text-xs text-slate-600 bg-white border border-stone-200 rounded-lg p-3" data-testid="myrto-welcome">
                Γεια σας! Είμαι η Μυρτώ και απαντώ με βάση τις καταχωρήσεις σας. Ρωτήστε με π.χ. «Τι δικασίμους έχω αυτή την εβδομάδα;», «Πότε λήγει η επόμενη προθεσμία μου;» ή «Πόσες εργασίες έχω σήμερα;».
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`myrto-message-${m.role}`} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'ml-auto bg-red-700 text-white' : 'bg-white border border-stone-200 text-slate-800'}`}>
                {m.text}
              </div>
            ))}
            {sending && (
              <div className="bg-white border border-stone-200 rounded-xl px-3 py-2 w-14 flex justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-purple-900" />
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-stone-200 p-2 flex items-center gap-2 bg-white">
            <input
              data-testid="myrto-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ρωτήστε τη Μυρτώ..."
              className="flex-1 h-10 px-3 text-sm border border-stone-300 rounded-lg bg-white outline-none focus:border-purple-700"
            />
            <button
              data-testid="myrto-send-btn"
              onClick={send}
              disabled={sending || !input.trim()}
              className="w-10 h-10 rounded-lg bg-purple-900 text-white flex items-center justify-center hover:bg-purple-950 disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MyrtoChat;
